/**
 * GettingStarted.tsx — public walkthrough page for new trail admins.
 *
 * Accessible at /getting-started both signed-in and signed-out. Mirrors
 * the docs/TRAIL_ADMIN_ONBOARDING.md SOP but visual: each step has a
 * compact CSS mockup of the actual UI the user will see, so they can
 * confirm "yes, that's the screen I'm on" without screenshots that go
 * stale every redesign. Mockups reuse the live app's CSS classes
 * (.card, .pill, StatusPill markup, etc.) so they stay visually in
 * sync as the theme evolves.
 */

interface Props {
  onBack?: () => void;
  isSignedIn: boolean;
}

export function GettingStarted({ onBack, isSignedIn }: Props) {
  return (
    <div className="getting-started">
      <header className="gs-hero">
        <img src="/apple-touch-icon.png" alt="" className="gs-hero-logo" />
        <h1>
          Trail Status<span className="pro">Pro</span> · Getting Started
        </h1>
        <p>
          A visual walkthrough for new trail administrators. Six steps from
          zero to posting your first status update. Plain English; no jargon.
        </p>
        {!isSignedIn && onBack && (
          <button className="secondary small" onClick={onBack}>
            ← Back to sign in
          </button>
        )}
      </header>

      <StepCard number={1} title="Sign in">
        <p>
          A curator invited you with your email. Open the sign-in page, type
          your email, and tap <strong>Send code</strong>.
        </p>
        <Mockup label="What you'll see">
          <MockSignIn />
        </Mockup>
        <p>
          You'll get an email with a <strong>6-digit code</strong> at the top
          (subject line also includes it for easy reading from the lock
          screen).
        </p>
        <Mockup label="Your sign-in email">
          <MockEmail />
        </Mockup>
        <p>
          Type the code into the form. You're signed in for{" "}
          <strong>365 days</strong> on this device.
        </p>
        <Aside>
          <strong>Why a code instead of just a link?</strong> Some email apps
          open links in the system browser, which can keep a separate cookie
          jar from a home-screen app you install later. Typing the code
          <em> inside</em> whichever surface you're using guarantees the
          sign-in lands in the right place.
        </Aside>
      </StepCard>

      <StepCard
        number={2}
        title="Install on your phone (recommended)"
      >
        <p>
          Adding the website to your home screen makes it feel like a regular
          app: standalone window, tap-to-open, your sign-in persists. Pick
          your platform below.
        </p>

        <div className="gs-platforms">
          <PlatformCard
            name="Android"
            browser="Chrome"
            steps={[
              "Tap the three-dot menu in the top right",
              "Tap \"Install app\" (or \"Add to Home Screen\")",
              "Confirm — the app icon appears on your home screen",
              "Tap the icon; your sign-in carries over automatically",
            ]}
          />
          <PlatformCard
            name="iPhone / iPad"
            browser="Safari"
            steps={[
              "Tap the Share button (square with the up arrow)",
              "Scroll down and tap \"Add to Home Screen\"",
              "Confirm — the app icon appears on your home screen",
              "Tap the icon, then sign in again from inside it",
            ]}
            note="iOS keeps Safari and standalone home-screen apps in separate cookie containers, so your sign-in doesn't carry across. Use the 6-digit code path (Step 1) inside the home-screen app to land the cookie in the right container."
          />
        </div>

        <Aside>
          Once installed, the app launches in standalone mode (no browser
          chrome, no address bar) and remembers you for a year regardless of
          platform.
        </Aside>
      </StepCard>

      <StepCard number={3} title="Find your trail in the catalog">
        <p>
          Tap <strong>My Trails</strong> in the top navigation. Initially
          your "Your trails" section will be empty — that's expected.
        </p>
        <p>
          Scroll down to <strong>Claim an existing trail</strong> and search
          for your trail by name.
        </p>
        <Mockup label="Search results look like this">
          <MockSearch />
        </Mockup>
        <Aside>
          <strong>Trail not in the catalog?</strong> Scroll further down to{" "}
          <strong>Propose a new trail</strong>, fill in the name and your
          status-page URL, then file a claim on the new trail. A curator will
          review both the trail and your claim.
        </Aside>
      </StepCard>

      <StepCard number={4} title="File a claim">
        <p>
          Tap <strong>File claim</strong> next to your trail. You'll be asked
          for a one-line reason — who you are and your role.
        </p>
        <Mockup label="A good reason includes your org + your function">
          <MockClaim />
        </Mockup>
        <p>
          The claim goes to a curator queue. Approval is usually same-day.
        </p>
      </StepCard>

      <StepCard number={5} title="Wait for curator approval">
        <p>
          When the curator approves, your trail moves into the{" "}
          <strong>Your trails</strong> section at the top of My Trails.
          Refresh the page periodically or wait — you don't need to do
          anything else here.
        </p>
        <Mockup label="Pending claims show on your dashboard">
          <MockPending />
        </Mockup>
      </StepCard>

      <StepCard number={6} title="Post a status">
        <p>This is the day-to-day. Once approved:</p>
        <ol className="gs-ol">
          <li>
            Open the app (home-screen icon or browser) and tap{" "}
            <strong>My Trails</strong>
          </li>
          <li>
            Find your trail under <strong>Your trails</strong>
          </li>
          <li>Choose status, write a summary, pick a TTL, tap <strong>Set status & push</strong></li>
        </ol>
        <Mockup label="The status override form">
          <MockStatusForm />
        </Mockup>
        <p>
          You'll see a confirmation:{" "}
          <em>"Status set to closed. Pushed to 42 device(s)."</em> The push
          fans out within seconds. Subscribers' iOS app rows update immediately
          and show a small <strong>"by Trail Admin"</strong> badge next to the
          status pill — your trust signal.
        </p>

        <h4 style={{ marginTop: 18 }}>Choosing a good TTL</h4>
        <table className="gs-mini-table">
          <thead>
            <tr><th>Situation</th><th>Suggested TTL</th></tr>
          </thead>
          <tbody>
            <tr><td>Quick weather closure</td><td>1–4 hours</td></tr>
            <tr><td>Storm closure, clears next morning</td><td>6–12 hours</td></tr>
            <tr><td>Full-day closure</td><td>24 hours</td></tr>
            <tr><td>Multi-day shutdown (construction, fire)</td><td>72 hours (max)</td></tr>
          </tbody>
        </table>

        <Aside>
          <strong>Status reverts to automatic when the TTL expires.</strong>{" "}
          You don't have to clear anything manually — Claude-driven
          classification takes back over on the next 30-minute cron tick.
          Tap <strong>Clear override</strong> if you want it to revert
          immediately.
        </Aside>
      </StepCard>

      <section className="card gs-section">
        <h3>Operational best practices</h3>
        <ul className="gs-ul">
          <li>
            <strong>Write meaningful summaries.</strong> "Closed" alone isn't
            great. "Closed — saturated from 2&quot; rain Sunday, checking
            tomorrow" is much better. The summary appears in the push, the
            app row, and the trail detail screen.
          </li>
          <li>
            <strong>Pick the right TTL.</strong> Too short → you're resetting
            every hour. Too long → automatic updates won't take over when
            reality has changed. Match the TTL to your real-world certainty
            horizon.
          </li>
          <li>
            <strong>Multiple admins per trail are supported.</strong> A primary
            steward and a deputy can both be admins. Any admin's override
            applies; most recent wins.
          </li>
          <li>
            <strong>The "by Trail Admin" badge is a trust signal.</strong>{" "}
            Subscribers see it when you post. Don't burn that trust with sloppy
            summaries or stale status.
          </li>
        </ul>
      </section>

      <section className="card gs-section">
        <h3>Troubleshooting</h3>
        <table className="gs-trouble-table">
          <thead>
            <tr><th>Symptom</th><th>Fix</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>After tapping the magic link in email, the home-screen app still asks for sign-in (mostly on iPhone)</td>
              <td>iOS keeps Safari and standalone home-screen apps in separate cookie containers. Use the 6-digit code path from inside the home-screen app instead. Android Chrome shares the cookie automatically — if you're on Android and seeing this, the install probably didn't complete; retry "Install app".</td>
            </tr>
            <tr>
              <td>"I don't see my trail under <em>Your trails</em>"</td>
              <td>Your claim hasn't been approved yet, or it was filed under a different email. Ping the curator.</td>
            </tr>
            <tr>
              <td>"I set a status but a friend with the iOS app didn't get a push"</td>
              <td>They haven't subscribed to your trail. Have them add it in the iOS app and toggle notifications on.</td>
            </tr>
            <tr>
              <td>"Code expired"</td>
              <td>Codes expire 15 minutes after request. Go back and ask for a new one.</td>
            </tr>
            <tr>
              <td>"Too many wrong codes"</td>
              <td>5 wrong tries invalidates the code. Request a new one.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="card gs-section">
        <h3>Quick reference</h3>
        <table className="gs-mini-table">
          <tbody>
            <tr><td>Code TTL</td><td className="mono">15 minutes</td></tr>
            <tr><td>Session TTL</td><td className="mono">365 days</td></tr>
            <tr><td>Status TTL range</td><td className="mono">1–72 hours (default 4)</td></tr>
            <tr><td>Status values</td><td className="mono">open · partially_open · closed · unknown</td></tr>
            <tr><td>Summary character limit</td><td className="mono">140</td></tr>
          </tbody>
        </table>
      </section>

      <footer className="gs-footer">
        Questions? Contact your curator. Updated 2026-05-27.
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

function StepCard({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="gs-step">
      <div className="gs-step-head">
        <div className="gs-step-num">{number}</div>
        <h2>{title}</h2>
      </div>
      <div className="gs-step-body">{children}</div>
    </section>
  );
}

function Aside({ children }: { children: React.ReactNode }) {
  return <div className="gs-aside">{children}</div>;
}

function Mockup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="gs-mockup">
      <div className="gs-mockup-label">{label}</div>
      <div className="gs-mockup-body">{children}</div>
    </div>
  );
}

function PlatformCard({
  name,
  browser,
  steps,
  note,
}: {
  name: string;
  browser: string;
  steps: string[];
  note?: string;
}) {
  return (
    <div className="gs-platform">
      <div className="gs-platform-head">
        <span className="gs-platform-name">{name}</span>
        <span className="gs-platform-browser">{browser}</span>
      </div>
      <ol className="gs-ol">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      {note && <div className="gs-platform-note">{note}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSS-only UI mockups — reuse live classes so they evolve with the theme.
// ---------------------------------------------------------------------------

function MockSignIn() {
  return (
    <div className="mock-signin">
      <img src="/apple-touch-icon.png" alt="" className="mock-logo" />
      <div className="mock-h1">
        Trail Status<span className="pro">Pro</span>
      </div>
      <div className="mock-p">Admin & curator console.</div>
      <div className="mock-input">you@example.com</div>
      <div className="mock-btn">Send code</div>
    </div>
  );
}

function MockEmail() {
  return (
    <div className="mock-email">
      <div className="mock-email-from">
        Trail Status Pro <span className="muted">&lt;info@trailstatuspro.com&gt;</span>
      </div>
      <div className="mock-email-subject">
        Trail Status Pro sign-in code: <strong>284913</strong>
      </div>
      <div className="mock-email-body">
        <div className="mock-email-code">2 8 4 9 1 3</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Paste this code into the Trail Status Pro admin app. Expires in 15 minutes.
        </div>
      </div>
    </div>
  );
}

function MockSearch() {
  return (
    <div className="mock-table">
      <div className="mock-row">
        <div>
          <strong>Blankets Creek</strong>
          <div className="muted" style={{ fontSize: 11 }}>SORBA Woodstock · GA</div>
        </div>
        <span className="pill open">open</span>
        <div className="mock-btn small">File claim</div>
      </div>
      <div className="mock-row">
        <div>
          <strong>Rope Mill</strong>
          <div className="muted" style={{ fontSize: 11 }}>SORBA Woodstock · GA</div>
        </div>
        <span className="pill partially_open">partial</span>
        <div className="mock-btn small">File claim</div>
      </div>
      <div className="mock-row">
        <div>
          <strong>Cochran Shoals</strong>
          <div className="muted" style={{ fontSize: 11 }}>Atlanta · GA</div>
        </div>
        <span className="pill null">unknown</span>
        <div className="mock-btn small">File claim</div>
      </div>
    </div>
  );
}

function MockClaim() {
  return (
    <div className="mock-prompt">
      <div className="mock-prompt-title">
        Why should you administer Blankets Creek?
      </div>
      <div className="mock-prompt-input">
        Trail steward for SORBA Woodstock, posts conditions weekly
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
        <span className="mock-btn small secondary">Cancel</span>
        <span className="mock-btn small">File claim</span>
      </div>
    </div>
  );
}

function MockPending() {
  return (
    <div className="mock-card-inner">
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Pending claims
      </div>
      <div className="mock-row" style={{ marginTop: 8 }}>
        <div><strong>Blankets Creek</strong></div>
        <div className="muted" style={{ fontSize: 12 }}>Filed today, 2:14 PM</div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        A curator will review your claim shortly.
      </div>
    </div>
  );
}

function MockStatusForm() {
  return (
    <div className="mock-status-form">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <strong>Blankets Creek</strong>
        <span>
          <span className="pill open">open</span>{" "}
          <span className="pill source">admin</span>
        </span>
      </div>

      <div className="mock-label">Status</div>
      <div className="mock-select">Closed ▾</div>

      <div className="mock-label">Summary (optional)</div>
      <div className="mock-input">
        CLOSED — saturated from 2&quot; rain Sunday, checking tomorrow
      </div>

      <div className="mock-label">TTL (hours)</div>
      <div className="mock-input" style={{ width: 80 }}>12</div>

      <div className="row" style={{ marginTop: 12 }}>
        <span className="mock-btn">Set status & push</span>
        <span className="mock-btn danger secondary">Clear override</span>
      </div>
    </div>
  );
}
